import React from 'react'
import PropTypes from 'prop-types'
import { Form, Input, InputNumber, Switch, Upload, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { SelectF } from '@/i2antdV5'

const FormItem = Form.Item
const { TextArea } = Input

const IMAGE_MAX_MB = 5

function toBool01 (raw) {
  return raw === true || raw === 1 || raw === '1'
}

function normUploadFileList (e) {
  if (Array.isArray(e)) return e
  return e && e.fileList ? e.fileList : []
}

/**
 * baseProps 契约：
 * - device：当前设备（回填表单）
 * - storeOptions：门店下拉（Redux 已加工为 [{ id, value }, ...]，直接交给 SelectF）
 */
class UpdateYingshiDeviceForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    baseProps: PropTypes.object
  }

  static defaultProps = {
    baseProps: {}
  }

  componentDidMount () {
    this.applyInitialValues()
  }

  componentDidUpdate (prevProps) {
    const prevId = prevProps.baseProps?.device?.id
    const nextId = this.props.baseProps?.device?.id
    if (prevId !== nextId) {
      this.applyInitialValues()
    }
  }

  buildInitialValues = () => {
    const { device } = this.props.baseProps || {}
    if (!device) return {}
    return {
      name: device.name != null ? String(device.name) : '',
      // storeOption.id 为字符串，与 SelectF value 对齐
      storeId:
        device.storeId == null || device.storeId === ''
          ? undefined
          : String(device.storeId),
      brand: device.brand != null ? String(device.brand) : '',
      location: device.location != null ? String(device.location) : '',
      bedNo: device.bedNo == null || device.bedNo === '' ? undefined : device.bedNo,
      isPinned: toBool01(device.isPinned),
      needInference: toBool01(device.needInference),
      notifyPhone: device.notifyPhone != null ? String(device.notifyPhone) : '',
      remark: device.remark != null ? String(device.remark) : '',
      sort: (() => {
        const n = Number(device.sort)
        return Number.isFinite(n) ? n : undefined
      })(),
      image: (() => {
        const raw = device.image || device.imageUrl
        const url = raw == null ? '' : String(raw).trim()
        if (!url) return []
        return [{ uid: '-1', name: 'image', status: 'done', url }]
      })()
    }
  }

  beforeUploadImage = (file) => {
    const type = file && file.type ? String(file.type) : ''
    const isImg =
      type === 'image/jpeg' ||
      type === 'image/png'
    if (!isImg) {
      message.error('仅支持 jpg / png')
      return Upload.LIST_IGNORE
    }
    const sizeMb = file.size / 1024 / 1024
    if (sizeMb >= IMAGE_MAX_MB) {
      message.error(`图片不能超过 ${IMAGE_MAX_MB}MB`)
      return Upload.LIST_IGNORE
    }
    return false
  }

  applyInitialValues = () => {
    const { form } = this.props
    if (!form || !form.current) return
    form.current.setFieldsValue(this.buildInitialValues())
  }

  render () {
    const { form, baseProps } = this.props
    const { storeOptions = [] } = baseProps || {}

    return (
      <Form ref={form} layout="vertical">
        <FormItem
          label="设备名称"
          name="name"
          rules={[
            { required: true, message: '请输入设备名称' },
            { max: 100, message: '名称不能超过100个字符' }
          ]}
        >
          <Input placeholder="设备名称" readOnly maxLength={100} />
        </FormItem>
        <FormItem noStyle shouldUpdate={(prev, cur) => prev.image !== cur.image}>
          {() => {
            const fileList = (form && form.current && form.current.getFieldValue('image')) || []
            return (
              <FormItem
                label="图片"
                name="image"
                valuePropName="fileList"
                getValueFromEvent={normUploadFileList}
                extra="支持 jpg / png ，最多 1 张，不超过 5MB"
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  beforeUpload={this.beforeUploadImage}
                >
                  {fileList.length >= 1 ? null : (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>上传</div>
                    </div>
                  )}
                </Upload>
              </FormItem>
            )
          }}
        </FormItem>
        <FormItem label="所属门店" name="storeId">
          <SelectF
            options={storeOptions}
            modalProps={{ placeholder: '选择门店' }}
          />
        </FormItem>
        <FormItem
          label="品牌"
          name="brand"
          rules={[{ max: 50, message: '品牌不能超过50个字符' }]}
        >
          <Input placeholder="如：萤石" readOnly maxLength={50} />
        </FormItem>
        <FormItem
          label="安装位置"
          name="location"
          rules={[{ max: 100, message: '位置不能超过100个字符' }]}
        >
          <Input placeholder="安装位置" allowClear maxLength={100} />
        </FormItem>
        <FormItem
          label="床号"
          name="bedNo"
          rules={[{ max: 50, message: '床号不能超过50个字符' }]}
        >
          <Input placeholder="床号" allowClear maxLength={50} />
        </FormItem>
        <FormItem
          label="通知手机号"
          name="notifyPhone"
          rules={[
            { max: 20, message: '不能超过20个字符' },
            { pattern: /^$|^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
          ]}
        >
          <Input placeholder="通知手机号" allowClear maxLength={20} />
        </FormItem>
        <FormItem label="置顶" name="isPinned" valuePropName="checked">
          <Switch checkedChildren="是" unCheckedChildren="否" />
        </FormItem>
        <FormItem label="需要推理" name="needInference" valuePropName="checked">
          <Switch checkedChildren="开" unCheckedChildren="关" />
        </FormItem>
        <FormItem label="序号" name="sort" extra="数字越小越靠前">
          <InputNumber placeholder="排序" precision={0} style={{ width: '100%' }} />
        </FormItem>
        <FormItem
          label="备注"
          name="remark"
          rules={[{ max: 500, message: '备注不能超过500个字符' }]}
        >
          <TextArea
            rows={3}
            placeholder="备注"
            allowClear
            showCount
            maxLength={500}
          />
        </FormItem>
      </Form>
    )
  }
}

export default UpdateYingshiDeviceForm
